import getIdFromIdIri from '../../shared/parsers/getIdFromIdIri';
import { unNestUntypedUsers } from './utils';

function userBaseParser(data: any): IUser {
  const getInitial = (value: string) => value.charAt(0).toUpperCase();
  const hasFirstAndLast = data.first_name && data.last_name;
  const fullName = hasFirstAndLast ? `${data.first_name} ${data.last_name}` : '';
  const fullNameWithInitial = hasFirstAndLast
    ? `${data.first_name} ${getInitial(data.last_name)}`
    : '';
  const initials = hasFirstAndLast
    ? `${getInitial(data.first_name)}${getInitial(data.last_name)}`
    : '';

  return {
    idIri: data['@id'],
    id: getIdFromIdIri(data['@id']),
    email: data.email ?? null,
    firstName: data.first_name ?? null,
    lastName: data.last_name ?? null,
    fullName,
    fullNameWithInitial,
    initials,
    enabled: data.enabled,
    hasPasswordSet: data.has_password_set,
    isManager: data.roles?.includes('ROLE_MANAGER') ?? false,
    isAdmin: data.roles?.includes('ROLE_ADMIN') ?? false,
    roles: data.roles ?? [],
    phoneNumber: data.phone_number ?? null,
    secondPhoneNumber: data.second_phone_number ?? null,
    username: data.username ?? null,
    createdAt: data.created_at ?? null,
    updatedAt: data.updated_at ?? null,
    deletedAt: data.deleted_at ?? null,
    enableNewsLetter: data.enable_newsletter,
    picturePath: data.picture_path ?? null,
  };
}
/**
 * Parse user from api data
 * @param {any} data - user data from api
 * @param {string} companyIdIri - company idIri of user
 * @returns {UserRequestResponse}
 * @returns {IUser} - returns.user : Parsed user
 * @returns {IMarketResearch | null} - returns.marketResearch : Parsed market research
 */
function userParser({ data }: { data: any }): UserParsedResponse {
  try {
    const user = userBaseParser(data);
    const c = data.company ?? null;
    const mr = data.market_research ?? null;

    const companyIdIri = !data.company ? null : typeof c === 'string' ? c : c['@id'];
    const subscriptionIdIri = c && c !== 'string' ? c.subscription : null;

    // parse market researchs
    const marketResearch = mr
      ? {
          idIri: mr['@id'],
          totalAds: mr.total_ads,
          totalNewAds: mr.total_ads_today,
          cities: null,
        }
      : null;

    return { user, companyIdIri, marketResearch, subscriptionIdIri };
  } catch (error: any) {
    throw new Error(error?.message);
  }
}

/**
 * Parse user from api data
 * @param {any} datas - users data from api
 * @param {string} companyIdIri - company idIri of users
 * @returns {Users} - Parsed users
 */
function usersParser(datas: any[]): Users {
  try {
    // map on all users
    const parsed = datas.map((m) => userParser({ data: m }) as UserParsedResponse);

    // take users only
    const users = parsed.map((m) => m.user);
    return users;
  } catch (error: any) {
    throw new Error(error?.message);
  }
}

function usersAsCollaboratorsParser(datas: any[], ownerIdIri: string) {
  try {
    const unNestedCollaborators = unNestUntypedUsers(datas);

    const parsed = unNestedCollaborators.map((m) => {
      const p = userBaseParser(m);
      return p;
    });

    const parsedWithoutAuthUser = parsed.filter((f) => f.idIri !== ownerIdIri);

    return parsedWithoutAuthUser;
  } catch (error) {
    return [];
  }
}

export { userBaseParser, userParser, usersAsCollaboratorsParser, usersParser };
